import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["checkbox", "selectAll", "bulkActionsBar", "selectedCount"]
  
  connect() {
    this.selectedIds = new Set()
    this.updateBulkActionsBar()
  }

  // Toggle individual checkbox
  updateSelection(event) {
    const checkbox = event.target
    const id = checkbox.value
    
    if (checkbox.checked) {
      this.selectedIds.add(id)
    } else {
      this.selectedIds.delete(id)
    }
    
    this.updateBulkActionsBar()
    this.updateSelectAllCheckbox()
  }

  // Toggle all checkboxes
  toggleAll(event) {
    const isChecked = event.target.checked
    
    this.checkboxTargets.forEach(checkbox => {
      checkbox.checked = isChecked
      const id = checkbox.value
      
      if (isChecked) {
        this.selectedIds.add(id)
      } else {
        this.selectedIds.delete(id)
      }
    })
    
    this.updateBulkActionsBar()
  }

  // Update bulk actions bar visibility and count
  updateBulkActionsBar() {
    const bulkActionsBar = document.getElementById("bulk-actions-bar")
    const selectedCount = document.getElementById("selected-count")
    
    if (this.selectedIds.size > 0) {
      bulkActionsBar.classList.remove("hidden")
      selectedCount.textContent = this.selectedIds.size
    } else {
      bulkActionsBar.classList.add("hidden")
    }
  }

  // Update select all checkbox state
  updateSelectAllCheckbox() {
    if (!this.hasSelectAllTarget) return
    
    const allChecked = this.checkboxTargets.length > 0 && 
                      this.checkboxTargets.every(checkbox => checkbox.checked)
    const someChecked = this.checkboxTargets.some(checkbox => checkbox.checked)
    
    this.selectAllTarget.checked = allChecked
    this.selectAllTarget.indeterminate = someChecked && !allChecked
  }

  // Handle bulk move action
  bulkMove(event) {
    if (this.selectedIds.size === 0) {
      alert("Please select at least one item")
      return
    }

    // Get current controller path from URL
    const pathParts = window.location.pathname.split('/')
    const isRoamer = pathParts.includes('roamers')
    const subscriberType = isRoamer ? 'roamer' : 'domestic'
    
    // Create and show modal for bulk move
    const modalHtml = `
      <div class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" id="bulk-move-modal">
        <div class="relative top-20 mx-auto p-5 border max-w-md shadow-lg rounded-md bg-white">
          <div class="mt-3">
            <div class="flex items-center justify-between mb-4">
              <h3 class="text-lg font-medium text-gray-900">Move ${this.selectedIds.size} IMEIs to Another List</h3>
              <button type="button" onclick="document.getElementById('bulk-move-modal').remove()" class="text-gray-400 hover:text-gray-600">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
            
            <form id="bulk-move-form" method="post" action="/admin/subscriber_lists/${subscriberType === 'roamer' ? 'roamers' : 'domestic_subscribers'}/bulk_move">
              ${Array.from(this.selectedIds).map(id => `<input type="hidden" name="ids[]" value="${id}">`).join('')}
              <input type="hidden" name="authenticity_token" value="${this.getCSRFToken()}">
              
              <div class="space-y-3">
                <div class="flex items-center">
                  <input type="radio" id="bulk_list_type_white" name="list_type" value="white" class="h-4 w-4 text-primary focus:ring-primary border-gray-300">
                  <label for="bulk_list_type_white" class="ml-2 block text-sm text-gray-700">
                    <span class="font-medium">White List</span>
                    <span class="text-xs text-gray-500 block">Approved devices allowed on the network</span>
                  </label>
                </div>

                <div class="flex items-center">
                  <input type="radio" id="bulk_list_type_black" name="list_type" value="black" class="h-4 w-4 text-primary focus:ring-primary border-gray-300">
                  <label for="bulk_list_type_black" class="ml-2 block text-sm text-gray-700">
                    <span class="font-medium">Black List</span>
                    <span class="text-xs text-gray-500 block">Blocked devices not allowed on the network</span>
                  </label>
                </div>

                <div class="flex items-center">
                  <input type="radio" id="bulk_list_type_gray" name="list_type" value="gray" class="h-4 w-4 text-primary focus:ring-primary border-gray-300">
                  <label for="bulk_list_type_gray" class="ml-2 block text-sm text-gray-700">
                    <span class="font-medium">Gray List</span>
                    <span class="text-xs text-gray-500 block">Devices under observation or review</span>
                  </label>
                </div>
              </div>

              <div class="mt-6 bg-yellow-50 border border-yellow-200 rounded-md p-4">
                <div class="flex">
                  <svg class="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
                  </svg>
                  <div class="ml-3">
                    <h3 class="text-sm font-medium text-yellow-800">Important</h3>
                    <p class="mt-1 text-sm text-yellow-700">
                      Moving ${this.selectedIds.size} IMEIs to another list will affect their network classification. 
                      This action will be logged for audit purposes.
                    </p>
                  </div>
                </div>
              </div>
              
              <div class="flex items-center justify-end space-x-3 mt-6">
                <button type="button" onclick="document.getElementById('bulk-move-modal').remove()" class="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors">
                  Cancel
                </button>
                <button type="submit" class="px-4 py-2 text-sm bg-primary text-white rounded-md hover:bg-primary-dark transition-colors">
                  Move ${this.selectedIds.size} IMEIs
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    `
    
    document.body.insertAdjacentHTML('beforeend', modalHtml)
  }

  // Handle bulk export action
  bulkExport(event) {
    if (this.selectedIds.size === 0) {
      alert("Please select at least one item")
      return
    }

    // Get current format from URL or default to CSV
    const pathParts = window.location.pathname.split('/')
    const isRoamer = pathParts.includes('roamers')
    const baseUrl = isRoamer ? '/admin/subscriber_lists/roamers' : '/admin/subscriber_lists/domestic_subscribers'
    
    // Create form for export
    const form = document.createElement('form')
    form.method = 'GET'
    form.action = `${baseUrl}.csv`
    
    // Add selected IDs
    this.selectedIds.forEach(id => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = 'ids[]'
      input.value = id
      form.appendChild(input)
    })
    
    // Submit form
    document.body.appendChild(form)
    form.submit()
    document.body.removeChild(form)
  }

  // Get CSRF token
  getCSRFToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ''
  }
}